import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import cloud from "d3-cloud";
import { useTranslation } from "react-i18next";

const TopWordsCloud = ({ dashboardData }) => {
  const { t } = useTranslation();
  const svgRef = useRef();
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const cloudData = dashboardData;

  useEffect(() => {
    if (!dashboardData || !dashboardData.months) return;

    const fetchKeywords = () => {
      setLoading(true);

      const keywordSet = new Set();

      dashboardData.months.forEach((month) => {
        if (Array.isArray(month.keywords)) {
          month.keywords.forEach((keyword) => {
            if (keyword?.trim()) {
              keywordSet.add(keyword.trim());
            }
          });
        }
      });

      const wordsArray = Array.from(keywordSet)
        .slice(0, 50)
        .map((text) => ({
          text,
          size: 30,
        }));

      setWords(wordsArray);
      setLoading(false);
    };

    fetchKeywords();
  }, [dashboardData]);

  useEffect(() => {
    if (!svgRef.current || loading || words.length === 0) return;

    const width = 500;
    const height = 260;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .append("g")
      .attr("transform", `translate(${width / 2},${height / 2})`);

    // Tooltip
    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "rgba(0,0,0,0.7)")
      .style("color", "white")
      .style("padding", "6px 10px")
      .style("border-radius", "6px")
      .style("pointer-events", "none")
      .style("opacity", 0)
      .style("z-index", 1000);

    const layout = cloud()
      .size([width, height])
      .words(words)
      .padding(5)
      .rotate(() => ~~(Math.random() * 2) * 90)
      .font("Impact")
      .fontSize((d) => d.size)
      .on("end", draw);

    layout.start();

    function draw(layoutWords) {
      svg
        .selectAll("text")
        .data(layoutWords)
        .enter()
        .append("text")
        .style("font-family", "Impact")
        .style(
          "fill",
          () => d3.schemeCategory10[Math.floor(Math.random() * 10)]
        )
        .style("cursor", "pointer")
        .attr("text-anchor", "middle")
        .attr("transform", (d) => `translate(${d.x},${d.y})rotate(${d.rotate})`)
        .style("font-size", (d) => `${d.size}px`)
        .text((d) => d.text)
        .on("mouseover", function (event, d) {
          tooltip.style("opacity", 1).html(`<strong>${d.text}</strong>`);
        })
        .on("mousemove", function (event) {
          tooltip
            .style("left", event.pageX + 10 + "px")
            .style("top", event.pageY - 20 + "px");
        })
        .on("mouseout", function () {
          tooltip.style("opacity", 0);
        });
    }

    return () => {
      tooltip.remove();
    };
  }, [words, loading]);

  return (
    <div className="bg-white rounded-md shadow-lg p-5">
      <h3 className="text-xl font-semibold text-primary mb-4">
        {t("dashboard.clientDashboard.TopWords")}
      </h3>
      <div className="rounded-lg h-[260px] p-2 flex items-center justify-center">
        {loading ? (
          <div className="text-gray-500">
            {t("button.loading") || "Loading..."}
          </div>
        ) : words.length === 0 ? (
          <div className="text-gray-500">
            {t("dashboard.clientDashboard.noAvailable")}
          </div>
        ) : (
          <svg ref={svgRef}></svg>
        )}
      </div>
    </div>
  );
};

export default TopWordsCloud;
